// Admin → API: which families may use the REST API and webhooks.
import { useCallback, useEffect, useState } from 'react';
import { api } from '../../lib/api';
import type { ApiFamily, ApiFamilyConfig } from '../../lib/types';
import { useParent } from '../ParentContext';

// --- API access governance -----------------------------------------------------------

/** Enable/disable the API per family and inspect each family's keys & webhooks. */
export function ApiAccess() {
  const { toast } = useParent();
  const [families, setFamilies] = useState<ApiFamily[] | null>(null);
  const [open, setOpen] = useState<string | null>(null);

  const load = useCallback(() => {
    api.get<{ families: ApiFamily[] }>('/api/admin/api-access').then((r) => setFamilies(r.families)).catch((e) => toast({ text: e.message, tone: 'alert' }));
  }, [toast]);
  useEffect(load, [load]);

  async function toggle(f: ApiFamily) {
    try {
      await api.put(`/api/admin/families/${f.id}/api`, { enabled: !f.apiEnabled });
      toast({ text: `API ${f.apiEnabled ? 'disabled' : 'enabled'} for ${f.name}.`, tone: 'good' });
      load();
    } catch (e) {
      toast({ text: (e as Error).message, tone: 'alert' });
    }
  }

  if (!families) return <p className="muted">Loading…</p>;

  return (
    <div className="stack">
      <p className="muted small">
        Turn the REST API and webhooks on or off for each family, and see the keys and webhooks they’ve created. Disabling a
        family stops all of its keys working and pauses its webhooks immediately. New families start with it off.
      </p>
      {families.length === 0 && <p className="muted">No families yet.</p>}
      {families.map((f) => (
        <div key={f.id} className="card stack">
          <div className="row-between">
            <span className="grow">
              <strong>{f.name}</strong>
              <span className="muted small block">
                {f.ownerName ? `${f.ownerName} · ${f.ownerEmail} · ` : ''}
                {f.keyCount} key{f.keyCount === 1 ? '' : 's'}, {f.webhookCount} webhook{f.webhookCount === 1 ? '' : 's'}
              </span>
            </span>
            <span className="row-gap">
              <span className={`tag ${f.apiEnabled ? 'feature' : ''}`}>{f.apiEnabled ? 'Enabled' : 'Disabled'}</span>
              <button className="btn small" onClick={() => setOpen(open === f.id ? null : f.id)}>
                {open === f.id ? 'Hide' : 'View config'}
              </button>
              <button className={`btn small ${f.apiEnabled ? 'danger-text' : 'primary'}`} onClick={() => toggle(f)}>
                {f.apiEnabled ? 'Disable' : 'Enable'}
              </button>
            </span>
          </div>
          {open === f.id && <ApiFamilyDetail familyId={f.id} reload={load} />}
        </div>
      ))}
    </div>
  );
}
function ApiFamilyDetail({ familyId, reload }: { familyId: string; reload: () => void }) {
  const { toast } = useParent();
  const [data, setData] = useState<ApiFamilyConfig | null>(null);

  const load = useCallback(() => {
    api.get<ApiFamilyConfig>(`/api/admin/families/${familyId}/api`).then(setData).catch((e) => toast({ text: e.message, tone: 'alert' }));
  }, [familyId, toast]);
  useEffect(load, [load]);

  async function revokeKey(id: string) {
    if (!confirm('Revoke this API key? Anything using it stops working.')) return;
    await api.del(`/api/admin/keys/${id}`).then(() => { load(); reload(); }).catch((e) => toast({ text: (e as Error).message, tone: 'alert' }));
  }
  async function removeHook(id: string) {
    if (!confirm('Remove this webhook?')) return;
    await api.del(`/api/admin/webhooks/${id}`).then(() => { load(); reload(); }).catch((e) => toast({ text: (e as Error).message, tone: 'alert' }));
  }

  if (!data) return <p className="muted small">Loading…</p>;
  const when = (ts: number | null) => (ts ? new Date(ts).toLocaleDateString() : 'never');

  return (
    <div className="sub-section stack">
      <h4 className="row-gap">API keys</h4>
      {data.keys.length === 0 && <p className="muted small">No keys.</p>}
      {data.keys.map((k) => (
        <div key={k.id} className="row-between list-row">
          <span className="grow">
            <strong>{k.name}</strong> <code className="muted">{k.prefix}…</code>
            <span className="muted small block">
              {k.ownerName} · {k.scopes.join(', ') || 'no scopes'} · created {when(k.createdAt)} · last used {when(k.lastUsedAt)}
            </span>
          </span>
          <button className="btn small danger-text" onClick={() => revokeKey(k.id)}>Revoke</button>
        </div>
      ))}
      <h4 className="row-gap">Webhooks</h4>
      {data.webhooks.length === 0 && <p className="muted small">No webhooks.</p>}
      {data.webhooks.map((w) => (
        <div key={w.id} className="row-between list-row">
          <span className="grow">
            <strong className="break">{w.url}</strong>
            <span className="muted small block">
              {w.ownerName} · {w.events.length ? w.events.join(', ') : 'all events'} · {w.disabled ? 'disabled' : 'active'}
            </span>
          </span>
          <button className="btn small danger-text" onClick={() => removeHook(w.id)}>Remove</button>
        </div>
      ))}
    </div>
  );
}
